/* Onboarding magic-link landing.
 * URL: /onboarding-start?token=... — wisselt token in bij backend en toont vervolgstap.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function OnboardingStartPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, ok: false, error: null, org_id: null, email: null });
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;
    const token = params.get("token");
    if (!token) {
      setState({ loading: false, ok: false, error: "Geen token in URL", org_id: null, email: null });
      return;
    }
    (async () => {
      try {
        const r = await api.post("/onboarding/magic-link/redeem", { token });
        setState({ loading: false, ok: true, error: null, org_id: r.data.organization_id, email: r.data.email });
      } catch (e) {
        setState({ loading: false, ok: false, error: formatApiError(e), org_id: null, email: null });
      }
    })();
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-neutral-50 to-sky-50">
      <Card className="max-w-md w-full p-8 bg-white shadow-lg" data-testid="onb-start-card">
        {state.loading && (
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin text-neutral-400 mx-auto mb-3" />
            <div className="text-neutral-600">Uitnodiging valideren…</div>
          </div>
        )}
        {!state.loading && state.ok && (
          <div className="text-center" data-testid="onb-start-ok">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-2xl font-semibold text-neutral-900 mb-2">Welkom bij Webster</h1>
            <p className="text-sm text-neutral-600 mb-6">
              Je uitnodiging is geverifieerd voor <strong>{state.email}</strong>. Log in of registreer om aan de onboarding te beginnen.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate(`/login?email=${encodeURIComponent(state.email)}`)}
                style={{ background: "var(--sage)" }} className="text-white" data-testid="onb-start-login">
                Doorgaan naar inloggen
              </Button>
              <Button variant="ghost" onClick={() => navigate(`/register?token=${encodeURIComponent(params.get("token") || "")}&email=${encodeURIComponent(state.email)}`)}
                data-testid="onb-start-register">
                Nog geen account? Registreren
              </Button>
            </div>
          </div>
        )}
        {!state.loading && !state.ok && (
          <div className="text-center" data-testid="onb-start-error">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
            <h1 className="text-xl font-semibold text-neutral-900 mb-2">Link niet geldig</h1>
            <p className="text-sm text-neutral-600 mb-4">{state.error}</p>
            <p className="text-xs text-neutral-500">Vraag je platformbeheerder om een nieuwe uitnodiging.</p>
          </div>
        )}
      </Card>
    </div>
  );
}
